import React from 'react';
import {
  type PoiSceneDef,
  POI_SCENE_MAP,
  getSceneKeyByLabel,
  DEFAULT_SCENE_KEY,
} from '../../../../config/poiSceneConfig';

interface PoiLegendItem {
  poiScene?: string;
  poi_scene?: string;
  count: number;
}

interface PoiLegendProps {
  data: PoiLegendItem[];
  visible: boolean;
  /** 当前激活的类型标签集合 */
  activeTypes: string[];
  /** 切换单个类型 */
  onToggleType: (label: string) => void;
  /** 全部打开/全部关闭 */
  onToggleAll: () => void;
}

// 读取 poiScene 字段（兼容 API 下划线命名 poi_scene）
const getSceneLabel = (item: PoiLegendItem): string => item.poiScene || item.poi_scene || '';

// 缓存 sceneKey → PoiSceneDef，避免重复查找
const categoryDefCache = new Map<string, PoiSceneDef>();

const getDef = (label?: string): PoiSceneDef => {
  if (!label) return POI_SCENE_MAP[DEFAULT_SCENE_KEY];
  const cached = categoryDefCache.get(label);
  if (cached) return cached;
  const key = getSceneKeyByLabel(label);
  const def = POI_SCENE_MAP[key] ?? POI_SCENE_MAP[DEFAULT_SCENE_KEY];
  categoryDefCache.set(label, def);
  return def;
};

const PoiLegend: React.FC<PoiLegendProps> = ({ data, visible, activeTypes, onToggleType, onToggleAll }) => {
  if (!visible || !data || data.length === 0) {
    return null;
  }

  // 按数量降序排列
  const sorted = [...data].sort((a, b) => b.count - a.count);
  const total = sorted.reduce((sum, item) => sum + item.count, 0);

  // 收集所有唯一类型标签
  const allLabels = Array.from(new Set(sorted.map(item => getSceneLabel(item))));
  const hasActive = activeTypes.length > 0;

  return (
    <div
      className="absolute bottom-4 left-48 z-[1000] animate-in fade-in slide-in-from-bottom-2 duration-200"
    >
      <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
          <div className="flex items-center gap-2">
            <button
              onClick={onToggleAll}
              className="flex items-center justify-center w-4 h-4 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors cursor-pointer"
              title={hasActive ? '全部取消' : '全部选中'}
            >
              <i className={`${hasActive ? 'ri-checkbox-multiple-line' : 'ri-checkbox-multiple-blank-line'} text-[11px]`} />
            </button>
            <span className="text-xs font-semibold text-slate-700">POI</span>
          </div>
          <span className="text-xs text-slate-400 font-medium">{total}项</span>
        </div>

        {/* 横向列表 */}
        <div className="grid grid-cols-3 gap-x-3 gap-y-1 px-3 py-2">
          {sorted.map((item, index) => {
            const label = getSceneLabel(item);
            const def = getDef(label);
            const isActive = activeTypes.includes(label);
            return (
              <div
                key={label || index}
                onClick={() => onToggleType(label)}
                className="inline-flex items-center gap-1.5 cursor-pointer transition-all rounded px-1.5 py-0.5 hover:bg-slate-100"
                style={{ opacity: isActive ? 1 : 0.35 }}
              >
                <div
                  className={`w-2 h-2 rounded-full shrink-0 ${def.bg}`}
                  style={{ backgroundColor: def.color } as React.CSSProperties}
                />
                <span className="text-xs text-slate-700 whitespace-nowrap">{label}</span>
                <span className={`text-[11px] font-semibold px-1 rounded ${def.text} ${def.bg.replace('-500', '-50')}`}>
                  {item.count}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PoiLegend;
